import React from "react";
import { tallyResultsForToday } from "@/utils/tally-results";
import MedalIcon from "@/components/icons/medal-icon";
import TrophyIcon from "@/components/icons/trophy-icon";

/**
 * Component to display the tally of wins and places
 * @param {Object} props
 * @param {Object} props.historicalData - The historical results data
 * @param {string} props.className - Additional classes to apply to the container
 * @param {string} props.trackName - Optional track name to show specific track stats
 * @param {string} props.raceKey - Optional race key to show specific race stats
 * @param {string} props.iconSize - Size of the icons (default: "w-4 h-4")
 * @param {string} props.textSize - Size of the text (default: "text-xs")
 * @param {number} props.tipCount - Number of tips to count (default: 2)
 */
const ResultsTally = ({
  historicalData,
  className = "",
  trackName = null,
  raceKey = null,
  iconSize = "w-4 h-4",
  textSize = "text-xs",
  tipCount = 2,
}) => {
  // Get the tally of wins and places, counting only the specified number of tips
  const tally = tallyResultsForToday(historicalData, tipCount);

  // For the overall tally, we only care if there are any wins or places
  if (
    !trackName &&
    !raceKey &&
    tally.total.wins === 0 &&
    tally.total.places === 0
  ) {
    return null;
  }

  // If a track name is specified, check if it has results
  if (trackName && !tally.tracksWithResults.has(trackName)) {
    return null;
  }

  // If a race key is specified, check if it has results
  if (raceKey && !tally.racesWithResults.has(raceKey)) {
    return null;
  }

  // Determine which data to display based on props
  let displayData = { wins: 0, places: 0 };

  if (raceKey && tally.byRace[raceKey]) {
    // Race-specific data
    displayData = tally.byRace[raceKey];
  } else if (trackName && tally.byTrack[trackName]) {
    // Track-specific data
    displayData = tally.byTrack[trackName];
  } else {
    // Total data
    displayData = tally.total;
  }

  // If there are no wins or places for the specific track/race, don't render
  if (displayData.wins === 0 && displayData.places === 0) {
    return null;
  }

  return (
    <div className={`results-tally inline-flex items-center ml-2 ${className}`}>
      {displayData.wins > 0 && (
        <div className="trophy-container flex items-center mr-2">
          <TrophyIcon fill="#F30099" className={iconSize} />
          <span className={`ml-1 ${textSize} font-medium`}>
            {displayData.wins}
          </span>
        </div>
      )}

      {displayData.places > 0 && (
        <div className="medal-container flex items-center">
          <MedalIcon className={iconSize} />
          <span className={`ml-1 ${textSize} font-medium`}>
            {displayData.places}
          </span>
        </div>
      )}
    </div>
  );
};

export default ResultsTally;
